import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, UserPlus, Search } from 'lucide-react';
import { apiService } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { TranslatedText } from '@/components/translated-text';

interface CustomerLoanSelectorProps {
  open: boolean;
  onClose: () => void;
  onSelectCustomer: (customer: any) => void;
  companyId: string;
}

export function CustomerLoanSelector({
  open,
  onClose,
  onSelectCustomer,
  companyId,
}: CustomerLoanSelectorProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  
  const [newCustomerForm, setNewCustomerForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'RW',
    // TIN is optional for loans
    tin: '',
  });

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setShowNewCustomerForm(false);
      setSearchTerm('');
      setNewCustomerForm({
        name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'RW',
        tin: '',
      });
    }
  }, [open]);

  useEffect(() => {
    if (open && companyId) {
      loadCustomers();
    }
  }, [open, companyId]);

  const loadCustomers = async () => {
    if (!companyId) {
      console.error('CustomerLoanSelector: No companyId provided');
      setCustomers([]);
      return;
    }

    setLoading(true);
    try {
      const data = await apiService.getCustomers({ companyId, pageSize: 100 });
      
      // API returns { items: [...], total, page, etc }
      const customerArray = data?.items || [];
      setCustomers(customerArray);
    } catch (error: any) {
      console.error('CustomerLoanSelector: Error loading customers:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load customers',
        variant: 'destructive',
      });
      // Set empty array on error to prevent filter issues
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCustomer = async () => {
    if (!newCustomerForm.name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Customer name is required',
        variant: 'destructive',
      });
      return;
    }

    setCreatingCustomer(true);
    try {
      const customer = await apiService.createCustomer({
        ...newCustomerForm,
        companyId,
        // TIN is optional, only send if provided
        tin: newCustomerForm.tin.trim() || undefined,
      });

      toast({
        title: 'Success',
        description: 'Customer created successfully. Please select the customer to continue.',
      });

      // Reload customers to show the new one
      await loadCustomers();
      
      // Go back to customer list and auto-select the new customer
      setShowNewCustomerForm(false);
      setSearchTerm(customer.name); // Search for the new customer
      
      // Reset form
      setNewCustomerForm({
        name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'RW',
        tin: '',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create customer',
        variant: 'destructive',
      });
    } finally {
      setCreatingCustomer(false);
    }
  };

  const filteredCustomers = Array.isArray(customers) 
    ? customers.filter(
        (customer) =>
          customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customer.phone?.includes(searchTerm)
      )
    : [];

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            <TranslatedText text="Select Customer for Loan" />
          </DialogTitle>
        </DialogHeader>

        {!showNewCustomerForm ? (
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Customer List */}
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : filteredCustomers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No customers found</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredCustomers.map((customer) => (
                  <button
                    key={customer.id}
                    onClick={() => {
                      onSelectCustomer(customer);
                      onClose();
                    }}
                    className="w-full text-left p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="font-medium">{customer.name}</div>
                    {customer.email && (
                      <div className="text-sm text-gray-600">{customer.email}</div>
                    )}
                    {customer.phone && (
                      <div className="text-sm text-gray-600">{customer.phone}</div>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* New Customer Button */}
            <Button
              onClick={() => setShowNewCustomerForm(true)}
              className="w-full"
              variant="outline"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              <TranslatedText text="Create New Customer" />
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* New Customer Form */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>
                  <TranslatedText text="Customer Name" /> *
                </Label>
                <Input
                  value={newCustomerForm.name}
                  onChange={(e) =>
                    setNewCustomerForm({ ...newCustomerForm, name: e.target.value })
                  }
                  placeholder="Enter customer name"
                />
              </div>

              <div>
                <Label>
                  <TranslatedText text="Email" />
                </Label>
                <Input
                  type="email"
                  value={newCustomerForm.email}
                  onChange={(e) =>
                    setNewCustomerForm({ ...newCustomerForm, email: e.target.value })
                  }
                  placeholder="customer@example.com"
                />
              </div>

              <div>
                <Label>
                  <TranslatedText text="Phone" />
                </Label>
                <Input
                  value={newCustomerForm.phone}
                  onChange={(e) =>
                    setNewCustomerForm({ ...newCustomerForm, phone: e.target.value })
                  }
                  placeholder="+250 XXX XXX XXX"
                />
              </div>

              <div className="col-span-2">
                <Label>
                  <TranslatedText text="Address" />
                </Label>
                <Input
                  value={newCustomerForm.address}
                  onChange={(e) =>
                    setNewCustomerForm({ ...newCustomerForm, address: e.target.value })
                  }
                  placeholder="Street address"
                />
              </div>

              <div>
                <Label>
                  <TranslatedText text="City" />
                </Label>
                <Input
                  value={newCustomerForm.city}
                  onChange={(e) =>
                    setNewCustomerForm({ ...newCustomerForm, city: e.target.value })
                  }
                  placeholder="City"
                />
              </div>

              <div>
                <Label>
                  <TranslatedText text="Country" />
                </Label>
                <Select
                  value={newCustomerForm.country}
                  onValueChange={(value) =>
                    setNewCustomerForm({ ...newCustomerForm, country: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="RW">Rwanda</SelectItem>
                    <SelectItem value="KE">Kenya</SelectItem>
                    <SelectItem value="UG">Uganda</SelectItem>
                    <SelectItem value="TZ">Tanzania</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* TIN field is hidden but optional */}
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowNewCustomerForm(false);
                  setNewCustomerForm({
                    name: '',
                    email: '',
                    phone: '',
                    address: '',
                    city: '',
                    state: '',
                    postalCode: '',
                    country: 'RW',
                    tin: '',
                  });
                }}
                disabled={creatingCustomer}
              >
                <TranslatedText text="Back" />
              </Button>
              <Button onClick={handleCreateCustomer} disabled={creatingCustomer}>
                {creatingCustomer ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    <TranslatedText text="Creating..." />
                  </>
                ) : (
                  <TranslatedText text="Create Customer" />
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
